import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, ShieldCheckIcon } from 'lucide-react';
import { FadeIn, StaggerContainer, StaggerItem } from './FadeIn';
function AnimatedCounter({
  end,
  suffix = '',
  prefix = '',
  decimals = 0
}: {
  end: number;
  suffix?: string;
  prefix?: string;
  decimals?: number;
}) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = 0;
    const duration = 2000;
    const increment = end / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(start);
      }
    }, 16);
    return () => clearInterval(timer);
  }, [end]);
  return <span data-id="element-124">
      {prefix}
      {count.toFixed(decimals)}
      {suffix}
    </span>;
}
export function HeroSection() {
  return <section className="relative min-h-screen flex flex-col justify-center pt-24 pb-16 overflow-hidden" data-id="element-125">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-grid-pattern opacity-30 z-0" data-id="element-126"></div>
      <div className="absolute inset-0 bg-radial-glow z-0" data-id="element-127"></div>

      {/* Decorative Nodes */}
      <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-teal/5 rounded-full blur-3xl animate-pulse-slow" data-id="element-128"></div>
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue/5 rounded-full blur-3xl animate-pulse-slow" style={{
      animationDelay: '2s'
    }} data-id="element-129"></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10 w-full flex-1 flex flex-col justify-center" data-id="element-130">
        <div className="max-w-4xl" data-id="element-131">
          <FadeIn delay={0.1} data-id="element-132">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal/10 border border-teal/20 text-teal text-sm font-medium" data-id="element-133">
              <ShieldCheckIcon className="w-4 h-4" data-id="element-134" />
              <span data-id="element-135">Post-Quantum Ready Infrastructure</span>
            </div>
          </FadeIn>

          <FadeIn delay={0.1} data-id="element-136">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal/10 border border-teal/20 text-teal text-sm font-medium" data-id="element-137">
              <ShieldCheckIcon className="w-4 h-4" data-id="element-138" />
              <span data-id="element-139">Micro-Lending</span>
            </div>
          </FadeIn>

          <FadeIn delay={0.1} data-id="element-140">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal/10 border border-teal/20 text-teal text-sm font-medium" data-id="element-141">
              <ShieldCheckIcon className="w-4 h-4" data-id="element-142" />
              <span data-id="element-143">AI Insights</span>
            </div>
          </FadeIn>

          <FadeIn delay={0.1} data-id="element-144">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal/10 border border-teal/20 text-teal text-sm font-medium" data-id="element-145">
              <ShieldCheckIcon className="w-4 h-4" data-id="element-146" />
              <span data-id="element-147">Core Integration Gateway</span>
            </div>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-148">
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-white mb-6 leading-[1.1]" data-id="element-149">
              Quantum-Ready Infrastructure for{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal to-blue" data-id="element-150">
                Modern Financial Institutions
              </span>
            </h1>
          </FadeIn>

          <FadeIn delay={0.3} data-id="element-151">
            <p className="text-xl text-gray-400 mb-10 max-w-2xl leading-relaxed" data-id="element-152">
              FIQIS delivers post-quantum security, AI-driven intelligence, and
              bank-owned revenue engines — all under your brand. Secure your
              future while unlocking new growth today.
            </p>
          </FadeIn>

          <FadeIn delay={0.4} className="flex flex-col sm:flex-row gap-4" data-id="element-153">
            <a href="mailto:arthur@fiqis.com?subject=Demo%20Request%20-%20FiQIS" className="bg-teal hover:bg-teal-400 text-navy font-semibold px-8 py-4 rounded-md transition-all duration-300 hover:shadow-[0_0_20px_rgba(0,212,170,0.4)] flex items-center justify-center gap-2 text-lg" data-id="element-154">
              Request a Demo
              <ArrowRightIcon className="w-5 h-5" data-id="element-155" />
            </a>
            <a href="#capabilities" className="bg-transparent border border-white/20 hover:border-white/50 text-white font-medium px-8 py-4 rounded-md transition-all duration-300 flex items-center justify-center text-lg hover:bg-white/5" data-id="element-156">
              Explore Platform
            </a>
          </FadeIn>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="relative z-10 mt-20 border-y border-white/10 bg-slate/50 backdrop-blur-sm" data-id="element-157">
        <div className="max-w-7xl mx-auto px-6 py-8" data-id="element-158">
          <StaggerContainer className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-4 divide-x-0 md:divide-x divide-white/10" data-id="element-159">
            <StaggerItem className="flex flex-col items-center md:items-start md:pl-8 first:pl-0 text-center md:text-left" data-id="element-160">
              <span className="text-3xl font-bold text-white mb-1" data-id="element-161">
                <AnimatedCounter end={99.99} decimals={2} suffix="%" data-id="element-162" />
              </span>
              <span className="text-sm text-gray-400 font-medium uppercase tracking-wider" data-id="element-163">
                Uptime SLA
              </span>
            </StaggerItem>
            <StaggerItem className="flex flex-col items-center md:items-start md:pl-8 text-center md:text-left" data-id="element-164">
              <span className="text-3xl font-bold text-white mb-1" data-id="element-165">Core</span>
              <span className="text-sm text-gray-400 font-medium uppercase tracking-wider" data-id="element-166">
                Integrations
              </span>
            </StaggerItem>
            <StaggerItem className="flex flex-col items-center md:items-start md:pl-8 text-center md:text-left" data-id="element-167">
              <span className="text-3xl font-bold text-white mb-1" data-id="element-168">
                NIST PQC
              </span>
              <span className="text-sm text-gray-400 font-medium uppercase tracking-wider" data-id="element-169">
                Compliant
              </span>
            </StaggerItem>
            <StaggerItem className="flex flex-col items-center md:items-start md:pl-8 text-center md:text-left" data-id="element-170">
              <span className="text-3xl font-bold text-white mb-1" data-id="element-171">
                Micro-Lending
              </span>
              <span className="text-sm text-gray-400 font-medium uppercase tracking-wider" data-id="element-172">
                Transactions Secured
              </span>
            </StaggerItem>
          </StaggerContainer>
        </div>
      </div>
    </section>;
}